import { AgEventAPI } from "@ai360/core";
import { MessageDescriptor } from "@ai360/core/intl";

import {
    filterLinkedProducts,
    isProductUnlinked,
} from "~/admin/setup/customer/agvance-linked-products";
import { IOption } from "~/core/components/select-input/model";
import { messages } from "../i18n-messages";

export interface IProductOptionsInput {
    formatMessage: (descriptor: MessageDescriptor, values?: Record<string, unknown>) => string;
    /** From useLinkedProductRestriction: null means unrestricted or not settled yet. */
    linkedProductGuids: Set<string> | null;
    products: AgEventAPI.IAvailableProduct[];
    selectedProductGuid: string | null;
    selectedProductName: string | null;
}

export interface IProductOptionsResult {
    isSelectedProductUnlinked: boolean;
    productOptions: IOption[];
}

const toOptions = (products: AgEventAPI.IAvailableProduct[]): IOption[] =>
    products.map((product) => ({ label: product.name, value: product.productGuid }));

/**
 * Builds the Defined Legend Product dropdown. An unlinked saved product stays in the list under its own
 * name so editing does not blank the field — the caller flags it via isSelectedProductUnlinked, not the label.
 */
export const getProductOptions = ({
    formatMessage,
    linkedProductGuids,
    products,
    selectedProductGuid,
    selectedProductName,
}: IProductOptionsInput): IProductOptionsResult => {
    const candidates = products ?? [];

    const isSelectedProductUnlinked = isProductUnlinked(selectedProductGuid, linkedProductGuids);

    if (linkedProductGuids == null) {
        return { isSelectedProductUnlinked, productOptions: toOptions(candidates) };
    }

    const options = toOptions(
        filterLinkedProducts(candidates, linkedProductGuids, (product) => product.productGuid)
    );

    if (!isSelectedProductUnlinked) {
        return { isSelectedProductUnlinked, productOptions: options };
    }

    const retainedProductName =
        candidates.find((product) => product.productGuid === selectedProductGuid)?.name ??
        selectedProductName ??
        formatMessage(messages.unknownProduct);

    return {
        isSelectedProductUnlinked,
        productOptions: [{ label: retainedProductName, value: selectedProductGuid }, ...options],
    };
};
